/** How many leading tags fit in `maxRows` given measured widths (incl. gaps). */
export const canFitTagWidths = (
  widths: number[],
  containerWidth: number,
  maxRows: number,
  gap = 4
): boolean => {
  if (containerWidth <= 0) return false;
  if (widths.length === 0) return true;

  let row = 0;
  let used = 0;

  for (const width of widths) {
    const w = Math.min(width, containerWidth);
    if (used === 0) {
      used = w;
      continue;
    }
    if (used + gap + w <= containerWidth) {
      used += gap + w;
    } else {
      row += 1;
      if (row >= maxRows) return false;
      used = w;
    }
  }

  return true;
};

/**
 * Largest tag count that fits in `maxRows`, reserving space for a "+N" rest chip
 * when not every tag can be shown.
 */
export const computeMaxTagCountForRows = (
  tagWidths: number[],
  containerWidth: number,
  options?: {
    maxRows?: number;
    gap?: number;
    restWidth?: number;
  }
): number => {
  const maxRows = options?.maxRows ?? 2;
  const gap = options?.gap ?? 4;
  const restWidth = options?.restWidth ?? 48;

  if (tagWidths.length === 0 || containerWidth <= 0) return 0;

  if (canFitTagWidths(tagWidths, containerWidth, maxRows, gap)) {
    return tagWidths.length;
  }

  for (let count = tagWidths.length - 1; count >= 0; count -= 1) {
    const widths = [...tagWidths.slice(0, count), restWidth];
    if (canFitTagWidths(widths, containerWidth, maxRows, gap)) {
      return count;
    }
  }

  return 0;
};

/**
 * Mirror styles aligned with FormSelect multiple tags so first-paint measurement
 * does not underestimate width before Ant styles fully apply.
 */
const MIRROR_TAG_STYLES = [
  'position:absolute',
  'left:-9999px',
  'top:0',
  'visibility:hidden',
  'pointer-events:none',
  'white-space:nowrap',
  'display:inline-flex',
  'align-items:center',
  'box-sizing:border-box',
  'height:35px',
  'line-height:32px',
  'font-size:14px',
  'font-weight:600',
  'padding-inline:8px',
  'border-radius:5px',
  'gap:4px',
].join(';');

/** Approximate Ant Select multiple-tag width (label + remove icon + padding). */
export const measureSelectTagWidth = (
  label: string,
  measureRoot: HTMLElement
): number => {
  const mirror = document.createElement('span');
  mirror.className = 'ant-select-selection-item';
  mirror.setAttribute('aria-hidden', 'true');
  mirror.style.cssText = MIRROR_TAG_STYLES;

  const content = document.createElement('span');
  content.className = 'ant-select-selection-item-content';
  content.textContent = label;
  mirror.appendChild(content);

  const remove = document.createElement('span');
  remove.className = 'ant-select-selection-item-remove';
  remove.textContent = '×';
  remove.style.cssText = 'font-size:12px;font-weight:800;display:inline-flex;';
  mirror.appendChild(remove);

  measureRoot.appendChild(mirror);
  const width = mirror.getBoundingClientRect().width;
  measureRoot.removeChild(mirror);

  // Fallback if measurement failed (narrow drawer / first paint).
  return width > 0 ? width : Math.ceil(label.length * 9 + 48);
};

/** Prefer real rendered tag widths when Ant has already painted them. */
export const measureRenderedOrMirrorTagWidths = (
  labels: string[],
  selector: HTMLElement
): number[] => {
  const rendered = Array.from(
    selector.querySelectorAll<HTMLElement>(
      '.ant-select-selection-overflow-item:not(.ant-select-selection-overflow-item-suffix):not(.ant-select-selection-overflow-item-rest) .ant-select-selection-item'
    )
  );

  return labels.map((label, index) => {
    const el = rendered[index];
    if (el) {
      const width = el.getBoundingClientRect().width;
      if (width > 0) return width;
    }
    return measureSelectTagWidth(label, selector);
  });
};
